import React, { useState } from 'react'
import { fetchWithCsrf } from '../utils/csrf'

interface User {
  id: number
  name: string
  email: string
}

interface RotationSlot {
  user: User
  status: 'picked' | 'current' | 'upcoming'
  position: number
}

interface Season {
  id: number
  club_id: number
  season_number: number
  status: 'not_started' | 'active' | 'complete'
  started_at?: string
  completed_at?: string
  current_rotation_index: number
  rotation_order: RotationSlot[]
  current_picker?: User
  book_picks_count: number
}

interface SeasonManagerProps {
  clubId: number
  season: Season | null
  currentUser: User
  onSeasonChange: () => void
}

export default function SeasonManager({
  clubId,
  season,
  currentUser,
  onSeasonChange
}: SeasonManagerProps) {
  const [creating, setCreating] = useState(false)
  const [starting, setStarting] = useState(false)
  const [skipping, setSkipping] = useState(false)

  const handleCreateSeason = async () => {
    setCreating(true)
    try {
      const response = await fetchWithCsrf(`/api/v1/clubs/${clubId}/seasons`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shuffle: true })
      })

      if (response.ok) {
        onSeasonChange()
      } else {
        const data = await response.json()
        console.error('Failed to create season:', data.error)
      }
    } catch (err) {
      console.error('Error creating season:', err)
    } finally {
      setCreating(false)
    }
  }

  const handleStartSeason = async () => {
    if (!season) return

    setStarting(true)
    try {
      const response = await fetchWithCsrf(`/api/v1/clubs/${clubId}/seasons/${season.id}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })

      if (response.ok) {
        onSeasonChange()
      } else {
        const data = await response.json()
        console.error('Failed to start season:', data.error)
      }
    } catch (err) {
      console.error('Error starting season:', err)
    } finally {
      setStarting(false)
    }
  }

  const handleSkipPicker = async () => {
    if (!season) return

    setSkipping(true)
    try {
      const response = await fetchWithCsrf(`/api/v1/clubs/${clubId}/seasons/${season.id}/skip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })

      if (response.ok) {
        onSeasonChange()
      } else {
        const data = await response.json()
        console.error('Failed to skip picker:', data.error)
      }
    } catch (err) {
      console.error('Error skipping picker:', err)
    } finally {
      setSkipping(false)
    }
  }

  // No season exists yet
  if (!season) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full mb-4">
            <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Active Season</h3>
          <p className="text-gray-600 mb-6">
            Start your first season to establish a reading rotation
          </p>
          <button
            onClick={handleCreateSeason}
            disabled={creating}
            className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/30"
          >
            {creating ? 'Creating...' : 'Start First Season'}
          </button>
        </div>
      </div>
    )
  }

  // Season exists but not started
  if (season.status === 'not_started') {
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Season {season.season_number}</h3>
            <p className="text-sm text-gray-500">Not started</p>
          </div>
          <button
            onClick={handleStartSeason}
            disabled={starting}
            className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/30"
          >
            {starting ? 'Starting...' : 'Start Season'}
          </button>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700 mb-3">Reading Order:</p>
          {season.rotation_order.map((slot) => (
            <div
              key={slot.position}
              className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
            >
              <span className="flex-shrink-0 w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-sm font-semibold text-gray-600">
                {slot.position}
              </span>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">{slot.user.name}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Active season
  if (season.status === 'active') {
    const isCurrentUserTurn = season.current_picker?.id === currentUser.id

    return (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Season {season.season_number}</h3>
            <p className="text-sm text-gray-500">
              {season.book_picks_count} of {season.rotation_order.length} picks made
            </p>
          </div>
        </div>

        {/* Current Picker Banner */}
        {season.current_picker && (
          <div className={`mb-6 p-4 rounded-lg ${isCurrentUserTurn ? 'bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200' : 'bg-gray-50'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">
                  {isCurrentUserTurn ? "It's your turn!" : 'Current Picker'}
                </p>
                <p className="text-lg font-bold text-gray-900">{season.current_picker.name}</p>
              </div>
              {!isCurrentUserTurn && (
                <button
                  onClick={handleSkipPicker}
                  disabled={skipping}
                  className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-white rounded-lg transition-colors border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {skipping ? 'Skipping...' : 'Skip Turn'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Rotation Order */}
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700 mb-3">Reading Order:</p>
          {season.rotation_order.map((slot) => (
            <div
              key={slot.position}
              className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                slot.status === 'current'
                  ? 'bg-gradient-to-r from-indigo-100 to-purple-100 border-2 border-indigo-300'
                  : slot.status === 'picked'
                  ? 'bg-green-50'
                  : 'bg-gray-50'
              }`}
            >
              <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                slot.status === 'current'
                  ? 'bg-indigo-600 text-white'
                  : slot.status === 'picked'
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {slot.status === 'picked' ? '✓' : slot.position}
              </span>
              <div className="flex-1">
                <p className={`text-sm font-medium ${
                  slot.status === 'current' ? 'text-indigo-900' : 'text-gray-900'
                }`}>
                  {slot.user.name}
                </p>
              </div>
              {slot.status === 'current' && (
                <span className="text-xs font-semibold text-indigo-600 bg-white px-2 py-1 rounded">
                  NOW
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Completed season
  if (season.status === 'complete') {
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Season {season.season_number} Complete!</h3>
          <p className="text-gray-600 mb-6">
            All {season.rotation_order.length} members have picked their books
          </p>
          <button
            onClick={handleCreateSeason}
            disabled={creating}
            className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/30"
          >
            {creating ? 'Creating...' : 'Start Next Season'}
          </button>
        </div>

        {/* Show completed rotation order */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-sm font-medium text-gray-700 mb-3">Final Reading Order:</p>
          <div className="space-y-2">
            {season.rotation_order.map((slot) => (
              <div
                key={slot.position}
                className="flex items-center gap-3 p-3 bg-green-50 rounded-lg"
              >
                <span className="flex-shrink-0 w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                  ✓
                </span>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{slot.user.name}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return null
}
